import React, { useRef, useEffect } from 'react';
import { Group, ActionIcon, Button, Box, Paper, Tooltip } from '@mantine/core';
import {
  IconArrowLeft,
  IconShare2,
  IconHeart,
  IconWand,
  IconInfoCircle,
  IconCornerUpLeft,
  IconPhotoEdit,
  IconTrash,
} from '@tabler/icons-react';
import { ImageDetails } from '../../../../types/Image';
import { DRAWER_WIDTH } from '../index';

interface ActionBarProps {
  onClose: () => void;
  metadata: ImageDetails | null;
  setActionBarHeight: (height: number) => void;
  drawerOpened: boolean;
  openDrawer: () => void;
}

export const ActionBar: React.FC<ActionBarProps> = ({ 
  onClose, 
  metadata,
  setActionBarHeight,
  drawerOpened,
  openDrawer
}) => {
  const actionBarRef = useRef<HTMLDivElement>(null);

  // Set up the action bar height measurement
  useEffect(() => {
    if (actionBarRef.current) {
      setActionBarHeight(actionBarRef.current.offsetHeight);
    }
  }, [setActionBarHeight]);

  return (
    <Paper
      id="actionBar"
      ref={actionBarRef}
      p="md"
      radius={0}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: drawerOpened ? DRAWER_WIDTH : 0,
        width: drawerOpened ? `calc(100% - ${DRAWER_WIDTH})` : '100%',
        zIndex: 10,
        backgroundColor: 'white',
        transition: 'width 300ms ease, right 300ms ease',
        borderBottom: '1px solid #ddd'
      }}
    >
      <Box style={{width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        {/* Left column - Back button */}
        <Box style={{width: '200px'}}>
          <Button
            variant="white"
            leftSection={<IconArrowLeft size={16}/>}
            onClick={onClose}
          >
            Back to Gallery
          </Button>
        </Box>

        {/* Center column - Action buttons */}
        <Group gap="xs" justify="center">
          <Tooltip label="Share Image">
            <ActionIcon variant="light" size="lg" color="blue">
              <IconShare2 size={20}/>
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Favorite Image">
            <ActionIcon
              variant="light"
              size="lg"
              color="blue"
            >
              <IconHeart size={20}/>
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Rotate Left">
            <ActionIcon
              variant="light"
              size="lg"
              color="blue"
            >
              <IconCornerUpLeft size={20}/>
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Auto Edit">
            <ActionIcon
              variant="light"
              size="lg"
              color="blue"
            >
              <IconWand size={20}/>
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Open Editor">
            <ActionIcon
              variant="light"
              size="lg"
              color="blue"
            >
              <IconPhotoEdit size={20}/>
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete Image">
            <ActionIcon
              variant="light"
              size="lg"
              color="red"
            >
              <IconTrash size={20}/>
            </ActionIcon>
          </Tooltip>
        </Group>

        {/* Right column - Info button or empty space */}
        <Box style={{width: '200px', display: 'flex', justifyContent: 'flex-end'}}>
          {!drawerOpened && (
            <Tooltip label="Show Metadata">
              <ActionIcon
                variant="filled"
                color="blue"
                size="lg"
                radius='xl'
                onClick={openDrawer}
              >
                <IconInfoCircle size={20}/>
              </ActionIcon>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
